import moment from "moment";
import { ReactNode, useState } from "react";
import {
  GetMeetingTranscriptQueryQuery,
  MeetingBotSetting,
  MeetingViewMeetingNodeNewPageFragmentFragment,
  PricingTier,
  RecallBotStatus,
} from "types/graphql-schema";

import { currentOrganizationVar } from "@cache/cache";
import BillingDialog from "@components/billing-dialog-cta/billing-dialog";
import Button from "@components/button/button";
import { ButtonSize, buttonTheme } from "@components/button/button-constants";
import { useLink } from "@components/link/link-helpers";
import Loading from "@components/loading/loading";
import { TopicflowIcon } from "@components/topicflow-icon/topicflow-icon";
import { classNames } from "@helpers/css";

import MeetingBot from "../meeting-bot";
import MeetingNotetakerCheckbox from "../notetaker-checkbox";

const MeetingSidebarAIContainer = ({
  meeting,
  transcriptMeeting,
  transcriptLoading,
  refetch,
  onDismiss,
  children,
}: {
  meeting: MeetingViewMeetingNodeNewPageFragmentFragment;
  transcriptMeeting: GetMeetingTranscriptQueryQuery["meeting"];
  transcriptLoading: boolean;
  refetch: () => void;
  // When provided, every actionable / informational state renders a small X
  // close button in its top-right that calls this. Not used by the loading
  // or success branches.
  onDismiss?: () => void;
  children: ({
    meeting,
    refetch,
    loading,
  }: {
    meeting: NonNullable<GetMeetingTranscriptQueryQuery["meeting"]>;
    refetch: () => void;
    loading: boolean;
  }) => ReactNode;
}) => {
  const link = useLink();
  const currentOrganization = currentOrganizationVar();
  const [showBillingDialog, setShowBillingDialog] = useState(false);

  const orgNotOnUltimatePlan =
    meeting.organization?.effectivePricingTier !== PricingTier.Ultimate;
  const summarizationNotEnabled =
    !meeting.organization?.enableMeetingSummarization;

  if (!meeting.organization || !meeting.meetingGroup) {
    return null;
  }

  const meetingHasNotStartedYet =
    meeting?.startDatetime && moment().isBefore(meeting.startDatetime);
  const meetingIsProbablyDone =
    meeting?.endDatetime &&
    moment().isAfter(moment(meeting.endDatetime).add(1, "hours"));
  const notetakerWillJoin =
    meeting.meetingGroup.meetingBotSetting === MeetingBotSetting.AlwaysJoin ||
    (meeting.meetingGroup.meetingBotSetting ===
      MeetingBotSetting.UseParticipantsSetting &&
      meeting.botParticipationDetails.willBotJoin);

  // - `pr-10` reserves room on the right so content doesn't slide under the
  //   absolute-positioned X button.
  // - `mb-12` replaces the bottom spacing that the parent SidebarSection used
  //   to provide (matches sectionSpacingClassName(true) in meeting-page.tsx).
  const containerClassName = classNames(
    "relative mt-4 bg-gray-100 p-4 rounded-lg text-gray-700 text-sm leading-relaxed",
    onDismiss && "pr-10 mb-12",
  );

  const dismissButton = onDismiss ? (
    <button
      type="button"
      onClick={onDismiss}
      aria-label="Dismiss"
      className="absolute top-3 right-3 p-1 rounded-md hover:bg-gray-200 text-gray-500"
    >
      <TopicflowIcon type="close" className="h-4 w-4" />
    </button>
  ) : null;

  return orgNotOnUltimatePlan ? (
    <>
      {showBillingDialog && (
        <BillingDialog
          organization={currentOrganization}
          onClose={() => setShowBillingDialog(false)}
        />
      )}
      <div className={containerClassName}>
        {dismissButton}
        <div className="tf-header-sm-dark">AI Summary Unavailable</div>
        <div>Upgrade your plan to access the AI Meeting Summary feature.</div>
        <div className="mt-4 flex">
          <Button
            text="Upgrade your plan"
            theme={buttonTheme.default}
            size={ButtonSize.medium}
            onClick={() => setShowBillingDialog(true)}
          />
        </div>
      </div>
    </>
  ) : summarizationNotEnabled ? (
    <div className={containerClassName}>
      {dismissButton}
      <div className="tf-header-sm-dark">AI Summary Unavailable</div>
      <div>
        Enable the AI Meeting Summarization in the organization settings to use
        this feature.
      </div>
      <div className="mt-4 flex">
        <Button
          text="View Organization Settings"
          theme={buttonTheme.default}
          size={ButtonSize.medium}
          onClick={() =>
            link.redirect(
              `/settings/organization/${meeting.organization?.id}/settings`,
            )
          }
        />
      </div>
    </div>
  ) : !transcriptMeeting && transcriptLoading ? (
    // Loading shares the same standalone-card layout as GeneratingSummary:
    // header + body on the left, small spinner pinned to the top-right.
    <div className={classNames(containerClassName, "pr-10 mb-12")}>
      <div className="absolute top-3 right-3 p-1">
        <Loading mini size={4} className="text-gray-500" />
      </div>
      <div className="tf-header-sm-dark">Loading meeting summary...</div>
      <div>Summary will be available shortly</div>
    </div>
  ) : !transcriptMeeting ? null : transcriptMeeting.transcript || // "Meeting not found" — section is hidden upstream in MeetingSummary.
    transcriptMeeting.notesSummary ? (
    children({
      meeting: transcriptMeeting,
      refetch,
      loading: transcriptLoading,
    })
  ) : transcriptMeeting.botStatus === RecallBotStatus.GeneratingSummary ? (
    // GeneratingSummary is transient — show a spinner in the top-right
    // instead of an X dismiss button. The standalone-card spacing (pr-10,
    // mb-12) is applied explicitly since onDismiss isn't provided here.
    <div className={classNames(containerClassName, "pr-10 mb-12")}>
      <div className="absolute top-3 right-3 p-1">
        <Loading mini size={4} className="text-gray-500" />
      </div>
      <div className="tf-header-sm-dark">
        Notetaker is processing the transcript
      </div>
      <div>
        The meeting recap will appear ~1 minute after the meeting concludes.
      </div>
    </div>
  ) : transcriptMeeting.botStatus === RecallBotStatus.InMeeting ? (
    <div className={containerClassName}>
      {dismissButton}
      <div>
        Notetaker is in the meeting, transcribing notes.
        <br />
        The meeting recap will appear ~1 minute after the meeting concludes.
      </div>
    </div>
  ) : transcriptMeeting.botStatus === RecallBotStatus.Scheduled ? (
    <div className={containerClassName}>
      {dismissButton}
      <div>
        Notetaker is scheduled to join the meeting at{" "}
        {moment(transcriptMeeting.startDatetime).format(
          "h:mma [on] dddd, MMMM D, YYYY",
        )}
        .
      </div>
    </div>
  ) : transcriptMeeting.botStatus === RecallBotStatus.JoiningMeeting ? (
    <div className={containerClassName}>
      {dismissButton}
      <div>
        Notetaker is trying to join the meeting.
        <br />
        Admit the notetaker to allow them to transcribe and summarize the
        meeting.
      </div>
    </div>
  ) : transcriptMeeting.isRecurring &&
    transcriptMeeting.meetingGroup?.canUpdate?.permission &&
    ((!meetingHasNotStartedYet && !meetingIsProbablyDone) ||
      !notetakerWillJoin) ? (
    // "No recap" with actionable CTA — show this so the user can add a
    // notetaker. The bare "no recap" fallback is hidden upstream.
    <div className={containerClassName}>
      {dismissButton}
      <div className="tf-header-sm-dark">No meeting summary available</div>
      <div>Add the notetaker to your meetings to get a meeting recap.</div>
      <div className="flex empty:hidden">
        {!meetingHasNotStartedYet && !meetingIsProbablyDone ? (
          <div className="mt-4 flex gap-4">
            <MeetingBot meeting={transcriptMeeting} />
          </div>
        ) : !notetakerWillJoin ? (
          <div className="mt-4 flex gap-4">
            <MeetingNotetakerCheckbox
              meeting={transcriptMeeting}
              className="flex items-center gap-1"
              label="Add the notetaker to future meetings"
            />
          </div>
        ) : null}
      </div>
    </div>
  ) : // Bare "No meeting recap available" — section is hidden upstream.
  null;
};

export default MeetingSidebarAIContainer;
